// Reusable client-side pagination — a hook that slices already-loaded rows into
// pages, plus a controls bar. Pairs with useTableSort (sort first, paginate the
// sorted result). Default page size is 25.
//
// Usage:
//   const pg = usePagination(sortedRows);          // 25/page
//   {pg.pageRows.map(...)}
//   <Pagination pg={pg} />

import { useMemo, useState } from "react";

export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

export interface Pager<T> {
  pageRows: T[];
  page: number; // clamped, 0-based
  setPage: (p: number) => void;
  pageSize: number;
  setPageSize: (n: number) => void;
  pageCount: number;
  total: number;
  /** 1-based index of the first row shown (0 when empty). */
  from: number;
  /** 1-based index of the last row shown. */
  to: number;
}

/** Paginate `rows` client-side. `pageSize` defaults to 25. The returned `page`
 *  is always clamped to the valid range, so shrinking the row set (e.g. after a
 *  filter) never strands you on an empty page. */
export function usePagination<T>(rows: T[], initialSize = 25): Pager<T> {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialSize);

  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const clamped = Math.min(Math.max(0, page), pageCount - 1);
  const start = clamped * pageSize;

  const pageRows = useMemo(() => rows.slice(start, start + pageSize), [rows, start, pageSize]);

  return {
    pageRows,
    page: clamped,
    setPage,
    pageSize,
    setPageSize: (n: number) => {
      setPageSize(n);
      setPage(0);
    },
    pageCount,
    total: rows.length,
    from: rows.length === 0 ? 0 : start + 1,
    to: Math.min(start + pageSize, rows.length),
  };
}

/** Footer controls for a {@link usePagination} pager: a row count, a page-size
 *  selector, and prev/next with the current page. Renders nothing when there's
 *  a single page AND the count is at/below the smallest page size (no clutter). */
export function Pagination<T>({ pg }: { pg: Pager<T> }) {
  if (pg.total <= PAGE_SIZE_OPTIONS[0] && pg.pageCount <= 1) return null;
  return (
    <div className="pagination">
      <span className="pagination-info">
        {pg.from}–{pg.to} of {pg.total}
      </span>
      <label className="pagination-size">
        <span>Per page</span>
        <select
          value={pg.pageSize}
          onChange={(e) => pg.setPageSize(Number(e.target.value))}
          aria-label="Rows per page"
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>
      <div className="pagination-nav">
        <button
          type="button"
          className="btn small"
          onClick={() => pg.setPage(pg.page - 1)}
          disabled={pg.page <= 0}
        >
          ← Prev
        </button>
        <span className="pagination-page">
          Page {pg.page + 1} of {pg.pageCount}
        </span>
        <button
          type="button"
          className="btn small"
          onClick={() => pg.setPage(pg.page + 1)}
          disabled={pg.page >= pg.pageCount - 1}
        >
          Next →
        </button>
      </div>
    </div>
  );
}
